import { timestampToSeconds, strictParseTimestamp } from 'web/utils/time-utils'
import { FightAssembler } from './fight-outline-importer'
import { Fight, FightSegment, FightEvent } from './models'
import { assertDefined } from 'web/utils/utils'

type ParsedFightParts = {
    fight: Fight
    segments: FightSegment[]
    events: FightEvent[]
}

/**
 * Quick'n dirty parser to ingest data from a Viserio sheet and
 * convert it to fight data.
 *
 * This will probably break really fast if anything ever shifts in the
 * spreadsheet, so keep an eye on it and don't assume anything.
 *
 * The biggest assumption is around column count and order. If either
 * of those shift we need to rewrite this to match.
 *
 * To use:
 * - Expand all hidden columns in the sheet
 * - Copy everything from name to the condition (https://i.imgur.com/XHQqdHd.png)
 * - Import here, add the line `!viserio` as the first line
 *
 * ---
 *
 * NOTICE: This parsing has one fatal flaw, which is that the sheet
 * format doesn't have the concept of "segments", and can therefore
 * "weave" events in a way that our format can't.
 *
 * What this means in practice is that the sheet can model two
 * segments running in parallel with their own dynamic timers. We
 * can't do that and will instead have to create a new segment each
 * time the condition changes.
 *
 * The way this parser is setup right now, that scenario isn't
 * handled. This is a deliberate choice since the cases where this is
 * ever needed are extremely rare and involve some absolutely
 * degenerate timers (ADH Council fight).
 */
export function parseViserioOutline(params: {
    fightOutlineString: string
    fightName?: string
}): ParsedFightParts {

    let { fightOutlineString } = params
    fightOutlineString = fightOutlineString.trim()
    fightOutlineString = fightOutlineString.replaceAll("\t", ";")

    const csvLines = fightOutlineString.split(/\r?\n/g)

    if (csvLines[0] !== "!viserio") {
        throw Error(`Invalid viserio import, expected first line ot be !viserio, was ${csvLines[0]}`)
    }

    const rawCsv = csvLines.map(line => line.split(";"))

    const processedCsv = rawCsv.flatMap((row, index) => {
        // Trim off the identifier row
        if (index === 0) {
            return []
        }

        const [name, _color, timestamp, _counting, _icon, condition, _dynamicTimer, _rawPhaseStart] = row

        // Dump untitled rows, they're usually just decorative
        if (name.trim() === '') {
            return []
        }

        return {
            name,
            timestamp,
            condition: (condition && condition.trim() !== '') ? condition : undefined
        }
    })

    const fightAssembler = FightAssembler.createNewFight({
        fightName: params.fightName ?? `Untitled ${Date.now()}`,
        firstSegmentName: `Segment 1`
    })

    let segmentCount = 1
    let latestCondition: string | undefined = undefined
    let prevTimestamp: string | undefined = undefined

    processedCsv.forEach(row => {
        if (row.condition !== latestCondition) {

            assertDefined(prevTimestamp, "Previous timestamp cannot be null")

            segmentCount += 1
            latestCondition = row.condition

            fightAssembler.pushSegment({
                name: `Segment ${segmentCount}`,
                fightOffset: timestampToSeconds(strictParseTimestamp(prevTimestamp)),
                condition: latestCondition
            })

        }

        fightAssembler.pushEvent({
            name: row.name,
            fightOffset: timestampToSeconds(strictParseTimestamp(row.timestamp)),
            flags: []
        })

        prevTimestamp = row.timestamp
    })

    const fight = fightAssembler.finish()

    return fight
}
